import { omit } from 'lodash'
import { DEFAULT_CLIENT_CONFIG } from '@src/fallbacks/fetchClientConfig'
import { transform } from '@src/transforms/clientConfig'
import { RootStoreState } from '@src/models/app.types'
import { TutorialKey } from '@screens/Education/types'
import Storage from '@src/utils/storage'
import { ConversationMessage, MobileAppFeature, UiStateNames, UserRole } from '@src/types'
import { EventsStoreState } from '@src/screens/Events/models/events.types'
import {
  DEFAULT_PREFERRED_HERO_CHART,
  HeroChartType,
} from '@src/screens/Settings/models/settings.types'
import { SupportHubStoreState } from '@src/screens/Profile/models/supportHub.types'
import { EVENTS_DEFAULT_SORT_STATE } from '@src/screens/Events/models/constants'

type OldRootStoreStateV41 = Omit<RootStoreState, 'events'> & {
  events: EventsStoreState & {
    dailyActions?: unknown
    dailyActionsCompletions?: unknown
  }
}

const clearCaches = (state: RootStoreState): RootStoreState => {
  const result = { ...state }
  Object.keys(state).forEach((key) => {
    result[key]['cache'] = {}
  })
  return result
}

const clearClientConfigCache = (state: RootStoreState): RootStoreState => {
  return clearCaches({
    ...state,
    app: {
      ...state.app,
      clientConfig: transform(DEFAULT_CLIENT_CONFIG).clientConfig,
    },
  })
}

const INITIAL_STATE: any = {}
const isHeroChartType = (value: unknown): value is HeroChartType =>
  Object.values(HeroChartType).includes(value as HeroChartType)

export const migrations: { [key: string]: (state: any) => RootStoreState } = {
  21: () => INITIAL_STATE, // Resetting the Redux store before Releasing 2.1.0 to Production
  22: (state: RootStoreState) => {
    const keysToOmit = ['lastSyncedBiosense', 'lastSyncedHealth', 'lastSyncedKetoMojo']
    return {
      ...state,
      app: {
        ...omit(state.app, keysToOmit),
        integrationsSyncSettings: {
          lastSyncedBiosense: null,
          lastSyncedHealth: null,
          lastSyncedKetoMojo: null,
        },
      },
    } as any
  },
  // mark education screens as visited for existing users
  23: (state: RootStoreState) => {
    const visitedTutorials = {
      [TutorialKey.CGMEducation]: true,
      [TutorialKey.GlucoseEducation]: true,
    }

    Storage.set(Storage.VISITED_TUTORIALS_KEY, visitedTutorials)

    return {
      ...state,
      education: {
        ...state.education,
        visitedTutorials,
      },
    }
  },
  24: (state: RootStoreState) => state,
  25: (state: RootStoreState) => {
    return {
      ...state,
      app: {
        ...state.app,
        clientConfig: {
          ...state.app.clientConfig,
          healthData: { ...DEFAULT_CLIENT_CONFIG.clientConfig.healthData },
        },
      },
      settings: {
        ...state.settings,
        dailyMeasurementsSourcePriorities: {},
      },
    } as any
  },
  26: clearClientConfigCache,
  27: (state: RootStoreState) => {
    return {
      ...state,
      app: {
        ...state.app,
        products: [],
        login: {
          ...state.app.login,
          user: {
            ...state.app.login.user,
            subscriptions: [],
          },
        },
      },
    } as any
  },
  29: (state: RootStoreState) => {
    Storage.set(Storage.PERMISSIONS_MODAL_VISITED_KEY, true)

    return state
  },
  30: (state: RootStoreState) => {
    return {
      ...state,
      app: {
        ...state.app,
        login: {
          ...state.app.login,
          user: {
            ...state.app.login.user,
            subscriptions: [],
          },
        },
      },
    } as any
  },
  31: (state: RootStoreState) => {
    return {
      ...state,
      marketplace: { promotionCode: null },
    } as any
  },
  32: (state: RootStoreState) => {
    Storage.set(Storage.WALKTHROUGH_SHOWN_KEY, true)
    return state
  },
  33: (state: RootStoreState) => {
    return {
      ...state,
      marketplace: { ...state.marketplace, subscriptionSchedules: [] },
    }
  },
  34: clearClientConfigCache,
  35: (state: RootStoreState) => {
    const user = state.app.login.user
    return {
      ...state,
      app: {
        ...state.app,
        login: {
          ...state.app.login,
          user: {
            ...user,
            uiStates:
              (user as any)?.signUpState === 'complete'
                ? [{ name: UiStateNames.CheckoutCompleted, value: true }]
                : user?.uiStates,
          },
        },
      },
    } as any
  },
  36: (state: RootStoreState) => {
    const user = state.app.login.user

    if (!user) {
      return state
    }

    return {
      ...state,
      app: {
        ...state.app,
        login: {
          ...state.app.login,
          user: {
            ...user,
            uiStates: (user.uiStates as any) || [],
          },
        },
      },
    }
  },
  37: (state: RootStoreState) => omit(state, ['experiments']) as RootStoreState,
  38: (state: RootStoreState) => {
    const user = state.app.login.user

    if (!user) {
      return state
    }

    const features =
      user.role === UserRole.User
        ? []
        : [MobileAppFeature.AdminAccess, MobileAppFeature.AdminImpersonate]

    return {
      ...state,
      app: {
        ...state.app,
        login: {
          ...state.app.login,
          features,
        },
      },
    }
  },
  39: (state: RootStoreState) => {
    const { refillConsents, ...stateWithoutRefillConsents } = state

    return {
      ...stateWithoutRefillConsents,
      policyConsents: refillConsents,
    }
  },
  40: clearClientConfigCache,
  41: (state: OldRootStoreStateV41) => {
    return ({
      ...state,
      events: {
        ...(omit(state.events, ['dailyActions', 'dailyActionsCompletions']) as EventsStoreState),
      },
      dailyActions: {
        preferredHeroChart: DEFAULT_PREFERRED_HERO_CHART,
      },
    } as unknown) as RootStoreState
  },
  42: (state: RootStoreState) => state,
  43: (state: RootStoreState) => {
    const legacyDailyActionsState = (state as any)?.dailyActions

    return ({
      ...state,
      dailyActions: {
        ...legacyDailyActionsState,
        preferredHeroChart: HeroChartType.DialChart,
      },
    } as unknown) as RootStoreState
  },
  44: (state: RootStoreState) => {
    return {
      ...state,
      events: {
        ...(omit(state.events, ['nutrition', 'statistics', 'score']) as EventsStoreState),
      },
    }
  },
  45: (state: RootStoreState) => {
    return {
      ...state,
      events: {
        ...(omit(state.events, ['charts']) as EventsStoreState),
      },
    }
  },
  46: (state: RootStoreState) => {
    return {
      ...(omit(state, ['history']) as RootStoreState),
      app: {
        ...state.app,
        clientConfig: transform(DEFAULT_CLIENT_CONFIG).clientConfig,
      },
      events: { ...state.history },
    }
  },
  47: clearClientConfigCache,
  48: (state: RootStoreState) => {
    return {
      ...state,
      supportHub: {
        ...omit(state.supportHub, ['conversationMessages']),
        messages:
          ((state.supportHub as SupportHubStoreState | undefined)?.messages as
            | ConversationMessage[]
            | undefined) || [],
      },
    } as any
  },
  49: (state: RootStoreState) => {
    const user = state.app.login.user

    if (!user) {
      return state
    }

    return {
      ...state,
      app: {
        ...state.app,
        login: {
          ...state.app.login,
          user: {
            ...user,
            features: user.features || [],
          },
        },
      },
    }
  },
  50: clearClientConfigCache,
  // Reset events sort to default values
  51: (state: RootStoreState) => {
    return {
      ...state,
      events: {
        ...state.events,
        sort: { ...EVENTS_DEFAULT_SORT_STATE },
      },
    } as any
  },
  52: clearClientConfigCache,
  // Backfill tags: [] on all persisted conversations
  53: (state: RootStoreState) => {
    const nora = (state as any).nora
    if (!nora?.chatbotConversations?.length) {
      return state
    }

    return {
      ...state,
      nora: {
        ...nora,
        chatbotConversations: nora.chatbotConversations.map((conversation: any) => ({
          ...conversation,
          tags: conversation.tags || [],
        })),
      },
    }
  },
  // Normalize persisted dailyActions slice shape to prevent runtime selector crashes.
  54: (state: RootStoreState) => {
    const persistedPreferredHeroChart = (state as any)?.dailyActions?.preferredHeroChart

    return ({
      ...state,
      dailyActions: {
        preferredHeroChart: isHeroChartType(persistedPreferredHeroChart)
          ? persistedPreferredHeroChart
          : DEFAULT_PREFERRED_HERO_CHART,
      },
    } as unknown) as RootStoreState
  },
  // Bust stale legacy Libre indirect-sync tutorial group data so newly published sensor
  // tutorials are fetched.
  55: (state: RootStoreState) => {
    const tutorials = (state as Partial<RootStoreState>).tutorials

    if (!Array.isArray(tutorials?.groups)) {
      return state
    }

    return {
      ...state,
      tutorials: {
        ...tutorials,
        // Migration 55 ran against the pre-`kind` schema where this tutorial group was the
        // numeric id 10. Hardcode the historical value here so the migration is stable now
        // that the enum has been re-keyed by CMS `kind`.
        groups: (tutorials.groups as Array<{ id?: string }>).filter((group) => group.id !== '10'),
      },
    } as RootStoreState
  },
  // Move persisted preferredHeroChart from legacy dailyActions slice into settings, then drop dailyActions.
  56: (state: RootStoreState) => {
    const legacyDailyActionsPreferredHeroChart = (state as any)?.dailyActions?.preferredHeroChart
    const existingSettingsPreferredHeroChart = (state as any)?.settings?.preferredHeroChart
    let preferredHeroChart = DEFAULT_PREFERRED_HERO_CHART

    if (isHeroChartType(legacyDailyActionsPreferredHeroChart)) {
      preferredHeroChart = legacyDailyActionsPreferredHeroChart
    } else if (isHeroChartType(existingSettingsPreferredHeroChart)) {
      preferredHeroChart = existingSettingsPreferredHeroChart
    }

    return {
      ...(omit(state, ['dailyActions']) as RootStoreState),
      settings: {
        ...state.settings,
        preferredHeroChart,
      },
    } as RootStoreState
  },
  // Drop the auth token from persisted redux — it now lives in the Keychain only.
  57: (state: RootStoreState) => {
    const login = (state as any)?.app?.login

    // Guard the type before `in`: a corrupt primitive login would otherwise throw and abort
    // rehydration at startup.
    if (!login || typeof login !== 'object' || !('token' in login)) {
      return state
    }

    return {
      ...state,
      app: {
        ...state.app,
        login: omit(login, ['token']),
      },
    } as RootStoreState
  },
  // Refetch client config so chatbot.disclosure is populated
  58: clearClientConfigCache,
}
